import { useState } from 'react'
import { isMatchTappable } from './TournamentBracketTree'
import type { BracketMatch, BracketSlot, RoundRobinPlan, RoundRobinRound } from './types'

const TIEBREAKER_LABELS: Record<string, string> = {
  wins: 'vitórias',
  score_diff: 'saldo de pontos',
  score_for: 'pontos pró',
  head_to_head: 'confronto direto',
  points: 'pontuação'
}

function tiebreakerLabel(id: string): string {
  return TIEBREAKER_LABELS[id] ?? id
}

interface TournamentRoundRobinViewProps {
  plan: RoundRobinPlan
  /** Result launching: called when a tappable match is clicked. */
  onMatchTap?: (match: BracketMatch) => void
  /** When true, drops the outer scroller so it can be embedded (e.g. one per
   * group in a group stage), and a composing parent scrolls instead. */
  bare?: boolean
  /** Draft-only: make the standings rows tappable so a parent can swap teams
   * (e.g. move a team between groups). Meaningless once results exist. */
  interactive?: boolean
  /** The team id currently picked for a swap (highlighted). */
  selectedTeamId?: string | null
  /** Called with a team id when its standings row is tapped (interactive only). */
  onTeamTap?: (teamId: string) => void
  /** Group stage: team ids that have qualified (draws a green accent). */
  qualifiedTeamIds?: string[]
  /** Group stage: team ids tied on the qualification boundary, awaiting the
   * organizer (amber "em disputa" accent). */
  disputingTeamIds?: string[]
}

/**
 * Round robin: the standings table (zeroed until results arrive) with the
 * configured tiebreaker order, and the fixture matchday by matchday. On its own
 * the draw is adjusted by redrawing; when `interactive`, the standings rows are
 * tappable so a group stage can swap a team between groups.
 */
export function TournamentRoundRobinView({
  plan,
  onMatchTap,
  bare = false,
  interactive = false,
  selectedTeamId = null,
  onTeamTap,
  qualifiedTeamIds,
  disputingTeamIds
}: TournamentRoundRobinViewProps) {
  const rowsTappable = interactive && !!onTeamTap
  const qualified = new Set(qualifiedTeamIds ?? [])
  const disputing = new Set(disputingTeamIds ?? [])

  // One round at a time (like a scoreboard), navigable — far tighter than laying
  // every round out in columns. Start on the first round still to be filled.
  const firstPending = plan.rounds.findIndex((r) => r.matches.some((m) => m.winnerTeamId == null))
  const [roundIdx, setRoundIdx] = useState(firstPending === -1 ? Math.max(0, plan.rounds.length - 1) : firstPending)
  const safeIdx = Math.min(Math.max(roundIdx, 0), plan.rounds.length - 1)
  const round = plan.rounds[safeIdx] as RoundRobinRound | undefined

  return (
    <div className={bare ? 'w-full' : 'h-full w-full overflow-y-auto pb-2'}>
      <div className="flex flex-wrap items-start gap-x-8 gap-y-6 w-full">
        <section className="w-full min-w-0 lg:flex-1">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Classificação</h3>
          <div className="h-7 flex items-center mb-3">
            <p className="text-xs text-gray-500">
              Desempate: {plan.tiebreakers.map(tiebreakerLabel).join(', ')}
              {plan.turns === 2 ? ' · ida e volta' : ' · turno único'}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto max-w-full">
            <table className="text-sm w-full min-w-[32rem]">
              <thead>
                <tr className="text-[11px] uppercase text-gray-500 border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-semibold">#</th>
                  <th className="px-3 py-2 text-left font-semibold w-56">Equipe</th>
                  <th className="px-3 py-2 text-center font-semibold">J</th>
                  <th className="px-3 py-2 text-center font-semibold">V</th>
                  <th className="px-3 py-2 text-center font-semibold">D</th>
                  <th className="px-3 py-2 text-center font-semibold">Pró</th>
                  <th className="px-3 py-2 text-center font-semibold">Contra</th>
                  <th className="px-3 py-2 text-center font-semibold">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {plan.standings.map((row) => {
                  const isSelected = selectedTeamId === row.team.id
                  const isQualified = qualified.has(row.team.id)
                  const isDisputing = !isQualified && disputing.has(row.team.id)
                  const accent = isQualified
                    ? 'border-l-2 border-emerald-500'
                    : isDisputing
                      ? 'border-l-2 border-amber-500'
                      : 'border-l-2 border-transparent'
                  return (
                  <tr
                    key={row.team.id}
                    onClick={rowsTappable ? () => onTeamTap?.(row.team.id) : undefined}
                    className={
                      isSelected
                        ? 'bg-brand-primary/10 cursor-pointer'
                        : rowsTappable
                          ? 'cursor-pointer transition-colors hover:bg-gray-50'
                          : isDisputing
                            ? 'bg-amber-50/60'
                            : ''
                    }
                  >
                    <td className={`px-3 py-2 text-gray-500 ${accent}`}>{row.position}</td>
                    <td className="px-3 py-2 text-gray-900 max-w-[14rem]">
                      <span className="truncate align-middle">{row.team.name}</span>
                      {isDisputing && (
                        <span className="ml-2 align-middle text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 rounded px-1 py-0.5 whitespace-nowrap">
                          em disputa
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-600">{row.played}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{row.wins}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{row.losses}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{row.scoreFor}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{row.scoreAgainst}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{row.scoreDiff}</td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>

        {round && (
          <section className="w-full lg:w-72 lg:flex-none">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Rodadas</h3>
            <div className="h-7 flex items-center gap-1 mb-3">
              <RoundNavButton
                label="Rodada anterior"
                disabled={safeIdx === 0}
                onClick={() => setRoundIdx(safeIdx - 1)}
              >
                ‹
              </RoundNavButton>
              <span className="text-xs font-semibold text-gray-500 uppercase w-[4.5rem] text-center tabular-nums">
                Rodada {round.round}
              </span>
              <RoundNavButton
                label="Próxima rodada"
                disabled={safeIdx === plan.rounds.length - 1}
                onClick={() => setRoundIdx(safeIdx + 1)}
              >
                ›
              </RoundNavButton>
            </div>
            <div className="space-y-2">
              {round.matches.map((match) => (
                <RoundMatchCard key={match.number} match={match} onMatchTap={onMatchTap} />
              ))}
              {restingTeams(plan, round).length > 0 && (
                <div className="text-xs text-gray-400 pt-0.5">
                  Folga: {restingTeams(plan, round).map((team) => team.name).join(', ')}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

/** Teams sitting out a round (odd fields have a bye each matchday). */
function restingTeams(plan: RoundRobinPlan, round: RoundRobinRound) {
  const playing = new Set(
    round.matches.flatMap((match) =>
      [match.top, match.bottom].flatMap((slot) => (slot.kind === 'team' && slot.team ? [slot.team.id] : []))
    )
  )
  return plan.standings.map((row) => row.team).filter((team) => !playing.has(team.id))
}

function RoundNavButton({
  children,
  label,
  disabled,
  onClick
}: {
  children: string
  label: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 text-gray-500 leading-none transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent"
    >
      {children}
    </button>
  )
}

/** One fixture line, laid out horizontally like a scoreboard:
 * `home  [x] × [y]  away`. Scores show once played; a walkover reads "W.O." */
function RoundMatchCard({ match, onMatchTap }: { match: BracketMatch; onMatchTap?: (match: BracketMatch) => void }) {
  const tappable = !!onMatchTap && isMatchTappable(match)
  const decided = match.winnerTeamId != null
  const homeWin = decided && match.top.team?.id === match.winnerTeamId
  const awayWin = decided && match.bottom.team?.id === match.winnerTeamId
  const hasScore = match.topScore != null && match.bottomScore != null

  const nameClass = (win: boolean) =>
    `flex-1 min-w-0 truncate ${win ? 'font-semibold text-gray-900' : decided ? 'text-gray-400' : 'text-gray-900'}`

  return (
    <div
      onClick={tappable ? () => onMatchTap?.(match) : undefined}
      className={`bg-white rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm flex items-center gap-2 ${
        tappable ? 'cursor-pointer transition-colors hover:border-brand-primary/60' : ''
      }`}
    >
      <div className={`${nameClass(homeWin)} text-right`}>{slotName(match.top)}</div>
      <div className="shrink-0 flex items-center gap-1">
        {match.walkover ? (
          <span className="text-[11px] font-semibold text-gray-500 px-1">W.O.</span>
        ) : hasScore ? (
          <>
            <ScoreBox value={match.topScore ?? 0} win={homeWin} />
            <span className="text-gray-300 text-xs">×</span>
            <ScoreBox value={match.bottomScore ?? 0} win={awayWin} />
          </>
        ) : (
          <span className="text-gray-300 px-2">×</span>
        )}
      </div>
      <div className={`${nameClass(awayWin)} text-left`}>{slotName(match.bottom)}</div>
    </div>
  )
}

function ScoreBox({ value, win }: { value: number; win: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded px-1 text-sm font-semibold tabular-nums ${
        win ? 'bg-brand-primary/10 text-gray-900' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {value}
    </span>
  )
}

/** A round-robin side is always a concrete team; falls back to a dash. */
function slotName(slot: BracketSlot): string {
  return slot.kind === 'team' && slot.team ? slot.team.name : '—'
}
